import type { PermissionDefinition, PermissionCategory } from './location-permissions.constants'
import { LOCATION_PERMISSION_CATEGORIES } from './location-permissions.constants'
import {
  SitePermissions,
  type SitePermissionsKey,
} from '../../../../schemas/users/enums/site-permissions'

const validSitePermissions = new Set<string>(Object.keys(SitePermissions))

function isSitePermissionKey(key: string): key is SitePermissionsKey {
  return validSitePermissions.has(key)
}

// Unlike IMPLICIT_DEPENDENCIES, an implied permission is also switched on and locked in the editor.
const IMPLIED_LOCATION_PERMISSIONS: Partial<Record<SitePermissionsKey, SitePermissionsKey[]>> = {
  schedule__sessions__add_and_edit: ['schedule__add_or_remove_booked_clients'],
}

function addImpliedPermissions(permissions: string[]): string[] {
  const withImplied = new Set(permissions)
  for (const permKey of permissions.filter(isSitePermissionKey)) {
    for (const implied of IMPLIED_LOCATION_PERMISSIONS[permKey] ?? []) {
      withImplied.add(implied)
    }
  }
  return Array.from(withImplied)
}

export function getImpliedByPermission(
  permissionKey: string,
  selectedPermissions: string[]
): string | undefined {
  if (!isSitePermissionKey(permissionKey)) {
    return undefined
  }
  return Object.entries(IMPLIED_LOCATION_PERMISSIONS).find(
    ([source, implied]) => selectedPermissions.includes(source) && implied?.includes(permissionKey)
  )?.[0]
}

export function buildParentMap(permissions: PermissionDefinition[]): Map<string, string[]> {
  const parentMap = new Map<string, string[]>()

  function traverse(perms: PermissionDefinition[], parents: string[] = []) {
    for (const perm of perms) {
      parentMap.set(perm.key, [...parents])

      if (perm.children && perm.children.length > 0) {
        traverse(perm.children, [...parents, perm.key])
      }
    }
  }

  traverse(permissions)
  return parentMap
}

export function getParentPermissions(
  permissionKey: string,
  permissions: PermissionDefinition[]
): string[] {
  const parentMap = buildParentMap(permissions)
  return parentMap.get(permissionKey) || []
}

export function expandPermissionsWithParents(
  selectedPermissions: string[],
  permissions: PermissionDefinition[]
): string[] {
  const parentMap = buildParentMap(permissions)
  const expandedSet = new Set<string>()

  for (const permKey of selectedPermissions) {
    expandedSet.add(permKey)

    const parents = parentMap.get(permKey) || []
    for (const parent of parents) {
      expandedSet.add(parent)
    }
  }

  return Array.from(expandedSet)
}

export function countSelectedInCategory(
  selectedPermissions: string[],
  category: PermissionCategory
): { selected: number; total: number } {
  const selectedSet = new Set(selectedPermissions)
  let selected = 0

  function countLeaves(perms: PermissionDefinition[]): number {
    let count = 0
    for (const perm of perms) {
      if (perm.children && perm.children.length > 0) {
        count += countLeaves(perm.children)
      } else {
        count += 1
        if (selectedSet.has(perm.key)) {
          selected += 1
        }
      }
    }
    return count
  }

  const total = countLeaves(category.permissions)
  return { selected, total }
}

export function countAllSelectedInCategory(
  selectedPermissions: string[],
  category: PermissionCategory
): { selected: number; total: number } {
  const selectedSet = new Set(selectedPermissions)
  let selected = 0
  let total = 0

  function countAll(perms: PermissionDefinition[]) {
    for (const perm of perms) {
      total += 1
      if (selectedSet.has(perm.key)) {
        selected += 1
      }
      if (perm.children && perm.children.length > 0) {
        countAll(perm.children)
      }
    }
  }

  countAll(category.permissions)
  return { selected, total }
}

export function getAllPermissionKeys(permissions: PermissionDefinition[]): string[] {
  const keys: string[] = []

  function collect(perms: PermissionDefinition[]) {
    for (const perm of perms) {
      keys.push(perm.key)
      if (perm.children && perm.children.length > 0) {
        collect(perm.children)
      }
    }
  }

  collect(permissions)
  return keys
}

export function hasSelectedChildren(
  permissionKey: string,
  selectedPermissions: string[],
  permissions: PermissionDefinition[]
): boolean {
  const selectedSet = new Set(selectedPermissions)

  function findAndCheck(perms: PermissionDefinition[]): boolean {
    for (const perm of perms) {
      if (perm.key === permissionKey && perm.children) {
        return perm.children.some(
          (child) =>
            selectedSet.has(child.key) ||
            hasSelectedChildren(child.key, selectedPermissions, perm.children || [])
        )
      }
      if (perm.children) {
        const found = findAndCheck(perm.children)
        if (found) {
          return true
        }
      }
    }
    return false
  }

  return findAndCheck(permissions)
}

export function getChildrenKeys(
  permissionKey: string,
  permissions: PermissionDefinition[]
): string[] {
  const children: string[] = []

  function findAndCollect(perms: PermissionDefinition[]): boolean {
    for (const perm of perms) {
      if (perm.key === permissionKey && perm.children) {
        collectChildren(perm.children)
        return true
      }
      if (perm.children) {
        if (findAndCollect(perm.children)) {
          return true
        }
      }
    }
    return false
  }

  function collectChildren(perms: PermissionDefinition[]) {
    for (const perm of perms) {
      children.push(perm.key)
      if (perm.children) {
        collectChildren(perm.children)
      }
    }
  }

  findAndCollect(permissions)
  return children
}

export function clearCategoryPermissions(
  currentPermissions: string[],
  category: PermissionCategory
): string[] {
  // A permission hidden behind a feature flag is missing from `category`, but on save it would
  // re-grant its parents, so turning the whole category off clears it from the full catalog too.
  const fullCategory = LOCATION_PERMISSION_CATEGORIES.find(({ id }) => id === category.id)
  const categoryKeys = new Set([
    ...getAllPermissionKeys(category.permissions),
    ...getAllPermissionKeys(fullCategory?.permissions ?? []),
  ])
  return currentPermissions.filter((key) => !categoryKeys.has(key))
}

export function togglePermission(
  permissionKey: string,
  currentPermissions: string[],
  allPermissions: PermissionDefinition[],
  isCurrentlySelected: boolean
): string[] {
  const currentSet = new Set(currentPermissions)

  if (isCurrentlySelected) {
    currentSet.delete(permissionKey)
    // A child hidden behind a feature flag is missing from `allPermissions`, but on save it would
    // re-grant the parent being turned off, so children are cleared from the full catalog too.
    const children = [
      ...getChildrenKeys(permissionKey, allPermissions),
      ...getChildrenKeys(
        permissionKey,
        LOCATION_PERMISSION_CATEGORIES.flatMap((category) => category.permissions)
      ),
    ]
    for (const child of children) {
      currentSet.delete(child)
    }
  } else {
    currentSet.add(permissionKey)
    // Select all children when turning on a parent
    const children = getChildrenKeys(permissionKey, allPermissions)
    for (const child of children) {
      currentSet.add(child)
    }
    // Also select all ancestor parents
    const parents = getParentPermissions(permissionKey, allPermissions)
    for (const parent of parents) {
      currentSet.add(parent)
    }
    return addImpliedPermissions(Array.from(currentSet))
  }

  return Array.from(currentSet)
}

export function isPermissionSelected(
  permissionKey: string,
  selectedPermissions: string[]
): boolean {
  return selectedPermissions.includes(permissionKey)
}

export function mapSitePermissionsToUI(
  apiPermissions: string[] | undefined,
  allPermissions?: PermissionDefinition[]
): string[] {
  if (!apiPermissions) {
    return []
  }
  const withImplied = addImpliedPermissions(apiPermissions)
  if (!allPermissions) {
    return withImplied
  }
  // Re-expand parent grouping keys so the UI hierarchy renders correctly
  return expandPermissionsWithParents(withImplied, allPermissions)
}

/**
 * Cross-category implicit dependencies.
 * When a permission is selected, its dependencies are automatically included.
 * This handles cases where a permission in one UI category requires a gateway
 * permission from another category to be functional (e.g. clients__client_password_reset
 * needs access__clients to even see the clients page).
 */
const IMPLICIT_DEPENDENCIES: Record<string, string[]> = {
  // Client features need access__clients (to see the clients page)
  clients__client_password_reset: ['access__clients'],
  payments__service_credit_adjustment: ['access__clients'],

  // Payment features need access__retail (to see the retail/payments page)
  payments__request_payment_access: ['access__retail'],
  payments__process_payments_within_schedule: ['access__retail'],
  payments__payroll_report_access: ['access__retail'],
  payments__access_to_cash_draw: ['access__retail'],
  payments__change_client_payment_method: ['access__retail', 'access__clients'],
  payments__send_payment_request_email: ['access__retail'],
  payments__retry_failed_payment_with_payment_method: ['access__retail'],
  payments__edit_dishonor_fee: ['access__retail'],
  payments__service_cancellation: ['access__retail'],
  payments__service_cancellation_notice_override: ['payments__service_cancellation'],
  payments__advanced_checkout: ['access__retail'],
  payments__bulk_edit_transactions: ['access__retail'],
  payments__invoice_reinstate: ['access__retail'],
  retail__sales__sell_outside_availability: ['access__retail'],
  retail__sales__sell_outside_dynamic_display: ['access__retail'],
  clients__bulk_edit_packages_expiry_date: ['access__retail'],

  // Report features need access__reports (to see the reports page)
  reports__client_and_attendance: ['access__reports'],
  reports__staff: ['access__reports'],
  reports__commissions: ['access__reports'],
  commissions__adjust: ['access__reports', 'reports__commissions'],
  commissions__attribution__edit: ['access__reports', 'reports__commissions'],
  reports__inventory: ['access__reports'],
  reports__financial: ['access__reports'],
  reports__edit_restricted_reports: ['access__reports'],
  reports__download: ['access__reports'],
  reports__policy_evidence_export: ['access__reports'],
}

/**
 * Maps UI permissions to site permissions for API submission.
 * Automatically includes 'access__settings' if any location permission is selected,
 * as it's required for accessing settings areas.
 * Also ensures all parent permissions are included when a child permission is selected,
 * so dependent permissions are always satisfied (belt-and-suspenders alongside togglePermission).
 */
export function mapUIToSitePermissions(uiPermissions: string[]): string[] {
  if (uiPermissions.length === 0) {
    return uiPermissions
  }

  const allPermissions = LOCATION_PERMISSION_CATEGORIES.flatMap((c) => c.permissions)
  const withParents = expandPermissionsWithParents(
    addImpliedPermissions(uiPermissions),
    allPermissions
  )
  const permissionsSet = new Set(withParents)

  // Add implicit cross-category dependencies
  for (const permKey of [...permissionsSet]) {
    const deps = IMPLICIT_DEPENDENCIES[permKey]
    if (deps) {
      for (const dep of deps) {
        permissionsSet.add(dep)
      }
    }
  }

  // If any location permission is selected, ensure access__settings is included
  if (!permissionsSet.has('access__settings')) {
    permissionsSet.add('access__settings')
  }

  // Filter out UI-only grouping keys (parent group headers) that don't exist in the backend enum
  return Array.from(permissionsSet).filter((key) => validSitePermissions.has(key))
}

export function findPermissionByKey(
  permissionKey: string,
  permissions: PermissionDefinition[]
): PermissionDefinition | undefined {
  for (const perm of permissions) {
    if (perm.key === permissionKey) {
      return perm
    }
    if (perm.children) {
      const found = findPermissionByKey(permissionKey, perm.children)
      if (found) {
        return found
      }
    }
  }
  return undefined
}

export function buildPermissionLabelMap(
  permissions: PermissionDefinition[]
): Record<string, string> {
  const labelMap: Record<string, string> = {}

  function traverse(perms: PermissionDefinition[]) {
    for (const perm of perms) {
      labelMap[perm.key] = perm.label
      if (perm.children && perm.children.length > 0) {
        traverse(perm.children)
      }
      if (perm.radioOptions) {
        for (const option of perm.radioOptions) {
          labelMap[option.value] = option.label
        }
      }
    }
  }

  traverse(permissions)
  return labelMap
}

export function getSitePermissionLabel(
  permissionKey: string,
  allCategories: PermissionCategory[]
): string {
  for (const category of allCategories) {
    const perm = findPermissionByKey(permissionKey, category.permissions)
    if (perm) {
      return perm.label
    }
  }
  return (
    permissionKey
      .split('__')
      .pop()
      ?.split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ') || permissionKey
  )
}

const withoutPermissionKey = (
  permissions: PermissionDefinition[],
  key: string
): PermissionDefinition[] =>
  permissions
    .filter((permission) => permission.key !== key)
    .map((permission) =>
      permission.children
        ? { ...permission, children: withoutPermissionKey(permission.children, key) }
        : permission
    )

export function withoutLocationPermission(
  categories: PermissionCategory[],
  key: string
): PermissionCategory[] {
  return categories.map((category) => ({
    ...category,
    permissions: withoutPermissionKey(category.permissions, key),
  }))
}
